import type {
  FarmingCalc,
  FarmingState,
  TokenAccountInfo,
  TokenSwapGetFarmedParams,
  TokenSwapLoadParams,
} from "@aldrin_exchange/sdk";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { convert, type MinimalTokenInfo } from "@stepfinance/common";
import type { AldrinFarmPosition } from "@stepfinance/farms/src/lib/positions/aldrin/types";
import type { AldrinLiquidityPool, LiquidityPoolMap, PendingReward } from "@stepfinance/liquidity";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type BigNumber from "bignumber.js";
import { useEffect, useState } from "react";

import { useAccountsContext } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import type { MarketsContextState } from "../../contexts/market";
import { useMarkets } from "../../contexts/market";
import { TokenSwap } from "../../utils/aldrin/tokenSwap";
import { priceInUSDWrapper } from "../../utils/markets/getPriceInUSD";
import { getTotalLiquidityInUSD } from "../../utils/pools/utils";
import { getTokenByMint } from "../../utils/tokenInfo";
import type { ConnectedWallet } from "../wallet/useConnectedWallet";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

type Farmed = {
  calcAccount: FarmingCalc | undefined;
  tokenInfo: TokenAccountInfo;
  state: FarmingState;
  userStakedAmount: BigNumber;
};

type FarmRewards = {
  rewards: { [tokenMint: string]: { rewardToken: MinimalTokenInfo; pendingReward: number; pendingRewardUSD: number } };
  poolMint: string;
  apr: number;
  userRatio: number;
};

const getFarmRewards = (farmed: Farmed[], priceInUSD: MarketsContextState["priceInUSD"], pool: AldrinLiquidityPool) => {
  const farmedRewards: FarmRewards = farmed.reduce((farmRewardsData: FarmRewards, farm) => {
    const rewardToken = getTokenByMint(farm.tokenInfo.mint.toBase58());
    if (!rewardToken) {
      return farmRewardsData;
    }
    const pendingReward = convert(farm.calcAccount?.tokenAmount, rewardToken.decimals);
    const pendingRewardUSD = pendingReward * priceInUSDWrapper(priceInUSD(rewardToken.address) ?? 0);

    const rewardTokenMint = rewardToken.address;
    const currentFarmRewards = farmRewardsData.rewards
      ? farmRewardsData.rewards[rewardTokenMint]
      : ({} as FarmRewards["rewards"][keyof FarmRewards["rewards"]]);
    const currentFarmPendingReward = currentFarmRewards?.pendingReward
      ? currentFarmRewards.pendingReward + pendingReward
      : pendingReward;
    const currentFarmPendingRewardUSD = currentFarmRewards?.pendingRewardUSD
      ? currentFarmRewards.pendingRewardUSD + pendingRewardUSD
      : pendingRewardUSD;
    const userRatio = pool.supply ? farm.userStakedAmount.div(pool.supply).toNumber() : 0;

    farmRewardsData = {
      ...farmRewardsData,
      apr: 0,
      userRatio,
      rewards: {
        ...farmRewardsData.rewards,
        [rewardTokenMint]: {
          ...currentFarmRewards,
          rewardToken,
          pendingReward: currentFarmPendingReward,
          pendingRewardUSD: currentFarmPendingRewardUSD,
        },
      },
    };

    return farmRewardsData;
  }, {} as FarmRewards);

  return farmedRewards;
};

const getAldrinFarms = async (
  connection: Connection,
  wallet: ConnectedWallet,
  aldrinPoolsMap: LiquidityPoolMap<"Aldrin">,
  userAccounts: NormalizedTokenAccount[],
  priceInUSD: MarketsContextState["priceInUSD"],
): Promise<AldrinFarmPosition[]> => {
  const aldrinTokenSwap = await TokenSwap.initialize({
    connection,
    wallet: wallet as unknown as TokenSwapLoadParams["wallet"],
  });

  const userStakedPools = userAccounts.filter((account) => {
    return Object.prototype.hasOwnProperty.call(aldrinPoolsMap, account.info.mint);
  });

  const stakedFarms = await Promise.all(
    userStakedPools.map(async (account) => {
      const pool = aldrinPoolsMap[account.info.mint];
      if (!pool) {
        return;
      }
      let farmed: Farmed[] = [];
      try {
        farmed = await aldrinTokenSwap.getFarmed({
          // TODO: Figure out why payer needs to be set for this,
          //        matching logic with typing in meantime
          wallet: wallet as unknown as TokenSwapGetFarmedParams["wallet"],
          poolMint: new PublicKey(pool.mintAddress),
        });
      } catch (err) {
        console.error("getAldrinFarms Error:", err);
      }

      if (farmed.length === 0) {
        return;
      }
      const { liquidity, liquidityInUSD, mintNames, mints } = pool;
      const totalLiquidityUSD = getTotalLiquidityInUSD(liquidityInUSD);
      const rewards: PendingReward[] = [];

      const farmedRewards = getFarmRewards(farmed, priceInUSD, pool);

      const liquidityComponents = liquidity.map((liq, index) => {
        return { amount: liq * farmedRewards.userRatio, token: getTokenByMint(mints[index]) };
      });

      const totalValue = farmedRewards.userRatio * totalLiquidityUSD;
      for (const key in farmedRewards.rewards) {
        const reward = farmedRewards.rewards[key];
        rewards.push({
          rewardToken: reward.rewardToken,
          rewardBalance: reward.pendingReward,
          rewardInUSD: reward.pendingRewardUSD,
        });
      }
      const farm: AldrinFarmPosition = {
        key: pool.accountAddress,
        kind: "Aldrin",
        lpName: mintNames.join("/"),
        link: pool.poolLink || "",
        description: "",
        liquidity: liquidityComponents,
        lpMintAddress: pool.mintAddress,
        rewards,
        apr: farmedRewards.apr,
        feesApr: 0,
        valueInUSD: totalValue,
      };

      return farm;
    }),
  );
  return stakedFarms.filter((x): x is AldrinFarmPosition => !!x);
};

export const useAldrinFarms = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const wallet = useConnectedWallet();
  const { userTokenAccounts } = useAccountsContext();
  const { priceInUSD, marketsInitialised } = useMarkets();

  const { aldrinPools } = useLiquidity();
  const [aldrinFarms, setAldrinFarms] = useState<AldrinFarmPosition[]>([]);

  useEffect(() => setAldrinFarms([]), [connected]);

  useEffect(() => {
    if (wallet && marketsInitialised) {
      void getAldrinFarms(connection, wallet, aldrinPools, userTokenAccounts, priceInUSD).then(setAldrinFarms);
    }
  }, [marketsInitialised, connection, aldrinPools, userTokenAccounts, publicKey, wallet, priceInUSD]);

  return aldrinFarms;
};
